import { Vec3, vec3, normalize, cross, scale, add, dot } from './vec3'
import { Mat3, mat3 } from './mat3'

/**
 * Quaternione unitario immutabile.
 * Per convenzione mappa dal sistema CORPO al sistema MONDO.
 */
export interface Quat {
  readonly x: number
  readonly y: number
  readonly z: number
  readonly w: number
}

export function quat(x = 0, y = 0, z = 0, w = 1): Quat {
  return { x, y, z, w }
}

export const QUAT_IDENTITY: Quat = quat(0, 0, 0, 1)

export function normalizeQuat(q: Quat): Quat {
  const n = Math.hypot(q.x, q.y, q.z, q.w)
  if (n === 0) return QUAT_IDENTITY
  const i = 1 / n
  return quat(q.x * i, q.y * i, q.z * i, q.w * i)
}

export function fromAxisAngle(axis: Vec3, angle: number): Quat {
  const a = normalize(axis)
  const h = angle / 2
  const s = Math.sin(h)
  return quat(a.x * s, a.y * s, a.z * s, Math.cos(h))
}

/** Prodotto di Hamilton: applica prima b, poi a. */
export function mulQuat(a: Quat, b: Quat): Quat {
  return quat(
    a.w * b.x + a.x * b.w + a.y * b.z - a.z * b.y,
    a.w * b.y - a.x * b.z + a.y * b.w + a.z * b.x,
    a.w * b.z + a.x * b.y - a.y * b.x + a.z * b.w,
    a.w * b.w - a.x * b.x - a.y * b.y - a.z * b.z,
  )
}

export const conjugate = (q: Quat): Quat => quat(-q.x, -q.y, -q.z, q.w)

/** Ruota un vettore dal sistema corpo al sistema mondo. */
export function rotate(q: Quat, v: Vec3): Vec3 {
  // v' = v + 2w(u × v) + 2(u × (u × v)),  u = parte vettoriale
  const u = vec3(q.x, q.y, q.z)
  const t = scale(cross(u, v), 2)
  return add(add(v, scale(t, q.w)), cross(u, t))
}

/** Ruota un vettore dal sistema mondo al sistema corpo. */
export const unrotate = (q: Quat, v: Vec3): Vec3 => rotate(conjugate(q), v)

/**
 * Integra l'orientamento data la velocita angolare in sistema CORPO.
 * q̇ = ½ q ⊗ (0, ω). Rinormalizza a ogni passo per evitare la deriva.
 */
export function integrateQuat(q: Quat, omegaBody: Vec3, dt: number): Quat {
  const w = quat(omegaBody.x, omegaBody.y, omegaBody.z, 0)
  const d = mulQuat(q, w)
  const h = dt * 0.5
  return normalizeQuat(quat(q.x + d.x * h, q.y + d.y * h, q.z + d.z * h, q.w + d.w * h))
}

export function quatToMat3(q: Quat): Mat3 {
  const { x, y, z, w } = q
  const x2 = x + x, y2 = y + y, z2 = z + z
  const xx = x * x2, xy = x * y2, xz = x * z2
  const yy = y * y2, yz = y * z2, zz = z * z2
  const wx = w * x2, wy = w * y2, wz = w * z2
  return mat3(
    1 - (yy + zz), xy - wz, xz + wy,
    xy + wz, 1 - (xx + zz), yz - wx,
    xz - wy, yz + wx, 1 - (xx + yy),
  )
}

/** Rotazione minima che porta il versore a sul versore b. */
export function fromTo(a: Vec3, b: Vec3): Quat {
  const na = normalize(a)
  const nb = normalize(b)
  const d = dot(na, nb)
  if (d >= 1 - 1e-12) return QUAT_IDENTITY
  if (d <= -1 + 1e-12) {
    // opposti: qualunque asse perpendicolare
    const axis = Math.abs(na.x) < 0.9 ? vec3(1, 0, 0) : vec3(0, 1, 0)
    return fromAxisAngle(normalize(cross(na, axis)), Math.PI)
  }
  const c = cross(na, nb)
  return normalizeQuat(quat(c.x, c.y, c.z, 1 + d))
}

/**
 * Angolo (rad) fra due orientamenti.
 * Si passa dalla rotazione relativa e da atan2, non da acos del prodotto
 * scalare: per angoli piccoli acos(1−ε) restituisce √(2ε) e butta via meta
 * delle cifre, proprio nel caso che interessa di piu — due orientamenti quasi
 * uguali, che e come si misura un errore.
 */
export function angleBetween(a: Quat, b: Quat): number {
  const r = mulQuat(conjugate(a), b)
  return 2 * Math.atan2(Math.hypot(r.x, r.y, r.z), Math.abs(r.w))
}

/**
 * Quaternione da una matrice di rotazione, col metodo di Shepperd: si sceglie
 * da quale componente partire in base alla piu grande, perche la formula
 * diretta perde precisione quando la traccia si avvicina a −1 — cioe sulle
 * rotazioni di mezzo giro, che non sono un caso di laboratorio.
 */
export function quatFromMat3(m: Mat3): Quat {
  const m00 = m[0]!, m01 = m[1]!, m02 = m[2]!
  const m10 = m[3]!, m11 = m[4]!, m12 = m[5]!
  const m20 = m[6]!, m21 = m[7]!, m22 = m[8]!
  const trace = m00 + m11 + m22

  if (trace > 0) {
    const s = Math.sqrt(trace + 1) * 2
    return normalizeQuat(quat((m21 - m12) / s, (m02 - m20) / s, (m10 - m01) / s, s / 4))
  }
  if (m00 > m11 && m00 > m22) {
    const s = Math.sqrt(1 + m00 - m11 - m22) * 2
    return normalizeQuat(quat(s / 4, (m01 + m10) / s, (m02 + m20) / s, (m21 - m12) / s))
  }
  if (m11 > m22) {
    const s = Math.sqrt(1 + m11 - m00 - m22) * 2
    return normalizeQuat(quat((m01 + m10) / s, s / 4, (m12 + m21) / s, (m02 - m20) / s))
  }
  const s = Math.sqrt(1 + m22 - m00 - m11) * 2
  return normalizeQuat(quat((m02 + m20) / s, (m12 + m21) / s, s / 4, (m10 - m01) / s))
}

/**
 * Quaternione che porta gli assi del corpo sulle tre immagini date.
 * Le immagini diventano le COLONNE della matrice di rotazione.
 */
export function quatFromBasis(xImage: Vec3, yImage: Vec3, zImage: Vec3): Quat {
  return quatFromMat3(mat3(
    xImage.x, yImage.x, zImage.x,
    xImage.y, yImage.y, zImage.y,
    xImage.z, yImage.z, zImage.z,
  ))
}
